import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Typography,
} from '@material-ui/core'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import CloseIcon from '@material-ui/icons/Close'
import GetAppIcon from '@material-ui/icons/GetApp'
import { toast } from 'react-toastify'
import clsx from 'clsx'

import {
    fetchCallRecording,
    useVoiceAgentCallDetail,
} from '../../../hooks/useVoiceAgentCalls'
import { usePhoneStyles } from '../../Phone/usePhoneStyles'
import { useVoiceAgentStyles } from '../useVoiceAgentStyles'
import { OutcomeChip } from '../../Phone/components/OutcomeChip'
import {
    CALL_CATEGORY_META,
    formatCallDateTime,
    formatDuration,
    formatE164,
    formatEndedReason,
} from '../constants'
import { parseTranscript } from './parseTranscript'

const useProxiedAudio = (callId: string | undefined, enabled: boolean) => {
    const [blobUrl, setBlobUrl] = useState<string | null>(null)
    const [failed, setFailed] = useState(false)
    const [expired, setExpired] = useState(false)

    useEffect(() => {
        if (!callId || !enabled) {
            setBlobUrl(null)
            setFailed(false)
            setExpired(false)
            return
        }
        let revoke: string | null = null
        setBlobUrl(null)
        setFailed(false)
        setExpired(false)

        fetchCallRecording(callId)
            .then((res) => {
                const objectUrl = URL.createObjectURL(res.data)
                revoke = objectUrl
                setBlobUrl(objectUrl)
            })
            .catch((err) => {
                if (err?.response?.status === 410) {
                    setExpired(true)
                } else {
                    setFailed(true)
                }
            })

        return () => {
            if (revoke) URL.revokeObjectURL(revoke)
        }
    }, [callId, enabled])

    return { blobUrl, failed, expired }
}

interface CallDetailPanelProps {
    callId: string
    onClose?: () => void
}

export const CallDetailPanel: React.FC<CallDetailPanelProps> = ({
    callId,
    onClose,
}) => {
    const phone = usePhoneStyles()
    const classes = useVoiceAgentStyles()
    const { call, isLoading, error } = useVoiceAgentCallDetail(callId)
    const [audioError, setAudioError] = useState(false)
    const [stereoDownloading, setStereoDownloading] = useState(false)

    const hasRecording = !!call?.has_recording
    const {
        blobUrl: audioBlobUrl,
        failed: audioFetchFailed,
        expired: audioExpired,
    } = useProxiedAudio(callId, hasRecording)

    useEffect(() => {
        setAudioError(false)
    }, [callId])

    const handleStereoDownload = async () => {
        setStereoDownloading(true)
        try {
            const res = await fetchCallRecording(callId, true)
            const url = URL.createObjectURL(res.data)
            const a = document.createElement('a')
            a.href = url
            a.download = `call-${callId}-stereo.mp3`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch {
            toast.error('Stereo recording unavailable.')
        } finally {
            setStereoDownloading(false)
        }
    }

    const handleTranscriptDownload = () => {
        if (!call?.transcript) return
        const blob = new Blob([call.transcript], { type: 'text/plain' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `call-${callId}-transcript.txt`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    if (isLoading) {
        return (
            <Box className={phone.card} p={4} textAlign="center">
                <CircularProgress size={28} />
            </Box>
        )
    }

    if (error || !call) {
        return (
            <Box className={phone.card} p={4}>
                <Typography color="textSecondary">
                    {error?.response?.status === 404
                        ? 'Call not found.'
                        : 'Unable to load call details.'}
                </Typography>
            </Box>
        )
    }

    return (
        <Box className={`${phone.card} ${phone.cardNoPadding}`}>
            <Box className={phone.detailHeader}>
                <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                >
                    <Box>
                        <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 600 }}
                        >
                            {call.tenant_name || formatE164(call.caller_number)}
                        </Typography>
                        {call.tenant_name ? (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginTop: 2 }}
                            >
                                {[
                                    call.tenant_location,
                                    formatE164(call.caller_number),
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </Typography>
                        ) : null}
                    </Box>
                    {onClose && (
                        <IconButton
                            size="small"
                            onClick={onClose}
                            aria-label="Close detail"
                            style={{
                                backgroundColor: '#f3f4f6',
                                padding: 5,
                                marginLeft: 8,
                            }}
                        >
                            <CloseIcon style={{ fontSize: 15 }} />
                        </IconButton>
                    )}
                </Box>
                <Box className={phone.detailMeta}>
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                        }}
                    >
                        <AccessTimeIcon style={{ fontSize: 12 }} />
                        {formatDuration(call.duration_sec)} ·{' '}
                        {formatCallDateTime(call.created_at)}
                    </span>
                </Box>

                <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                    {(() => {
                        const cat =
                            CALL_CATEGORY_META[call.call_category] ??
                            CALL_CATEGORY_META['']
                        return (
                            <Chip
                                label={cat.label}
                                size="small"
                                style={{
                                    height: 20,
                                    fontSize: 11,
                                    fontWeight: 500,
                                    backgroundColor: cat.bg,
                                    color: cat.color,
                                }}
                            />
                        )
                    })()}
                    {call.call_topic && (
                        <Chip
                            label={call.call_topic}
                            size="small"
                            style={{
                                height: 20,
                                fontSize: 11,
                                fontWeight: 500,
                                backgroundColor: '#f3f4f6',
                                color: '#374151',
                            }}
                        />
                    )}
                </Box>

                {call.call_category === 'GENERAL_SOLICITATION' &&
                    call.followup_reason && (
                        <Box mt={1}>
                            <Typography
                                variant="body2"
                                style={{
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    color: '#92400e',
                                }}
                            >
                                Why they called
                            </Typography>
                            <Typography
                                variant="body2"
                                style={{
                                    fontSize: '0.8125rem',
                                    color: '#78350f',
                                    marginTop: 2,
                                }}
                            >
                                {call.followup_reason}
                            </Typography>
                        </Box>
                    )}

                {hasRecording && (
                    <Box mt={2}>
                        {audioExpired ? (
                            <Typography variant="body2" color="textSecondary">
                                This recording has expired and is no longer
                                available.
                            </Typography>
                        ) : audioError || audioFetchFailed ? (
                            <Typography variant="body2" color="textSecondary">
                                Recording unavailable.
                            </Typography>
                        ) : audioBlobUrl ? (
                            <>
                                <audio
                                    controls
                                    src={audioBlobUrl}
                                    style={{ width: '100%' }}
                                    onError={() => setAudioError(true)}
                                />
                                {call.stereo_recording_url && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        className={classes.btnSmall}
                                        startIcon={<GetAppIcon />}
                                        onClick={handleStereoDownload}
                                        disabled={stereoDownloading}
                                        style={{ marginTop: 8 }}
                                    >
                                        {stereoDownloading
                                            ? 'Downloading...'
                                            : 'Download recording'}
                                    </Button>
                                )}
                            </>
                        ) : (
                            <CircularProgress size={20} />
                        )}
                    </Box>
                )}

                <Box className={phone.chipRow}>
                    <OutcomeChip
                        label={formatEndedReason(call.ended_reason)}
                        tone="neutral"
                    />
                    <OutcomeChip label="AI handled" tone="brand" />
                    {call.lead_id && (
                        <OutcomeChip label="Lead created" tone="positive" />
                    )}
                </Box>
            </Box>

            <Box className={phone.detailBody}>
                <Box className={phone.transcriptPanel}>
                    <Box className={phone.transcriptHeader}>
                        <Typography className={phone.sectionLabel}>
                            Transcript
                        </Typography>
                        {call.has_transcript && call.transcript && (
                            <button
                                type="button"
                                className={phone.downloadLink}
                                onClick={handleTranscriptDownload}
                            >
                                Download
                            </button>
                        )}
                    </Box>
                    {call.has_transcript && call.transcript ? (
                        parseTranscript(call.transcript).map((turn, i) => {
                            const isUser = turn.speaker === 'user'
                            return (
                                <Box
                                    key={i}
                                    className={clsx(
                                        phone.transcriptLine,
                                        isUser && phone.transcriptLineUser,
                                    )}
                                >
                                    <Box
                                        className={clsx(
                                            phone.transcriptAvatar,
                                            turn.speaker === 'ai'
                                                ? phone.transcriptAvatarAi
                                                : phone.transcriptAvatarUser,
                                        )}
                                    >
                                        {turn.speaker === 'ai' ? 'AI' : 'C'}
                                    </Box>
                                    <Box
                                        className={clsx(
                                            phone.transcriptBubble,
                                            turn.speaker === 'ai'
                                                ? phone.transcriptBubbleAi
                                                : phone.transcriptBubbleUser,
                                        )}
                                    >
                                        {turn.text}
                                    </Box>
                                </Box>
                            )
                        })
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            No transcript available.
                        </Typography>
                    )}
                </Box>

                <Box className={phone.summaryPanel}>
                    <Typography className={phone.sectionLabel}>
                        AI summary
                    </Typography>
                    <Typography className={phone.summaryText}>
                        {call.summary || 'No summary for this call.'}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}
